import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, AlertCircle, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProcedureCard from '@/pages/procedures/index/_partials/ProcedureCard';
import { ProceduresGridSkeleton } from '@/pages/procedures/index/_partials/ProcedureSkeletons';
import { useAdminProcedureTemplates } from '@/lib/hooks/use-admin-procedure-templates';
import { useAuth } from '@/lib/contexts/auth-context';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import SheetDiffusion from '../sheets/SheetDiffusion';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

function generateAbbreviation(name) {
    if (!name) return '??';
    const words = name.split(' ').filter((w) => w.length > 2);
    if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).charAt(0).toUpperCase() + name.substring(1, 2).toLowerCase();
}

function transformTemplate(template) {
    return {
        id: template.uniq_id,
        uniq_id: template.uniq_id,
        name: template.name,
        color: template.color || 'rgb(74, 144, 226)',
        abbreviation: generateAbbreviation(template.name),
        description: template.description || '',
    };
}

// ── Mock (mettre à false pour utiliser l'API) ──
const USE_MOCK = false;

function getMockTemplates() {
    return [
        {
            uniq_id: 'mock-admin-1',
            name: i18n.t('account:constatPhotographiqueDeDommage'),
            description:
                i18n.t('account:procedureCompletePourConstaterEtDocumenterDesDom'),
            color: 'rgb(74, 144, 226)',
        },
        {
            uniq_id: 'mock-admin-2',
            name: i18n.t('account:etatDesLieuxLocatif'),
            description: i18n.t('account:formulaireStandardisePourLesEtatsDesLieuxD'),
            color: 'rgb(52, 220, 167)',
        },
        {
            uniq_id: 'mock-admin-3',
            name: i18n.t('account:controleDeConformiteChantier'),
            description: i18n.t('account:suiviDesControlesDeConformiteSurChantierAvec'),
            color: 'rgb(255, 107, 107)',
        },
    ];
}

const MOCK_COMPANY_NAME = 'Objectif 54';

export default function AdminProceduresContent() {
    const { t } = useTranslation();
    const MOCK_TEMPLATES = getMockTemplates();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [diffusionProcedure, setDiffusionProcedure] = useState(null);
    const [subsidiaries, setSubsidiaries] = useState([]);

    const companyId = user?.company_uniq_id ?? user?.company?.uniq_id ?? null;

    const loadSubsidiaries = useCallback(async () => {
        if (!companyId) return;
        try {
            const res = await horodatyApi.subsidiaries.list(companyId);
            const list = res?.data ?? res?.data?.data ?? [];
            setSubsidiaries(Array.isArray(list) ? list : []);
        } catch {
            // silently fail — subsidiaries are optional context
        }
    }, [companyId]);

    useEffect(() => {
        loadSubsidiaries();
    }, [loadSubsidiaries]);

    const {
        templates: apiTemplates,
        companyName: apiCompanyName,
        isLoading: apiLoading,
        error: apiError,
        refetch,
    } = useAdminProcedureTemplates();

    const rawTemplates = USE_MOCK ? MOCK_TEMPLATES : apiTemplates;
    const companyName = USE_MOCK ? MOCK_COMPANY_NAME : apiCompanyName;
    const isLoading = USE_MOCK ? false : apiLoading;
    const error = USE_MOCK ? null : apiError;

    const procedures = rawTemplates.map(transformTemplate);

    const handleCreate = () => {
        navigate('/procedures/create?type=admin&returnTo=/settings/superadmin/procedures');
    };

    return (
        <div className="p-5 space-y-4">
            {/* Header + bouton créer */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-[15px] font-semibold text-foreground">{t('account:proceduresTypes')}</h3>
                    <p className="text-[13px] text-muted-foreground mt-0.5">
                        {t('account:proceduresModelesDisponiblesPourTousLesSousCompt')}
                    </p>
                </div>
                <Button variant="solid" color="primary" onClick={handleCreate} className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('account:creerUneProcedureType')}</span>
                </Button>
            </div>

            {/* Loading */}
            {isLoading && <ProceduresGridSkeleton count={6} />}

            {/* Erreur */}
            {error && !isLoading && (
                <div className="text-center py-8">
                    <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm mb-3">{t('account:erreurLorsDuChargement')}</p>
                    <Button variant="outline" color="neutral" size="sm" onClick={() => refetch(true)}>
                        {t('account:reessayer')}
                    </Button>
                </div>
            )}

            {/* Empty state */}
            {!isLoading && !error && procedures.length === 0 && (
                <div className="text-center py-12">
                    <p className="text-muted-foreground text-sm">{t('account:aucuneProcedureTypeCreee')}</p>
                    <p className="text-muted-foreground text-xs mt-1">
                        {t('account:creezVotrePremiereProcedureTypePourLaRendre')}
                    </p>
                </div>
            )}

            {/* Grid */}
            {!isLoading && !error && procedures.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[15px]">
                    {procedures.map((procedure) => (
                        <div key={procedure.uniq_id} className="relative">
                            <ProcedureCard
                                procedure={procedure}
                                onClick={() => navigate(`/procedures/${procedure.uniq_id}`)}
                                isAdmin
                            />
                            <Button
                                variant="outline"
                                color="neutral"
                                size="auto"
                                className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-white shadow-sm"
                                title={t('account:diffusion2')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDiffusionProcedure(procedure);
                                }}
                            >
                                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {/* Sheet diffusion */}
            <SheetDiffusion
                open={!!diffusionProcedure}
                onClose={() => setDiffusionProcedure(null)}
                procedureName={diffusionProcedure?.name || ''}
                companies={subsidiaries}
                companyName={companyName}
            />
        </div>
    );
}
